import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import partnerRoutes from "./routes/partnerRoutes";
import authRoutes from "./routes/authRoutes";
import { useSessionStore } from "@/stores/session";
import { Permissions } from "@/constants/access";
import { checkIfArrayElementsMatch } from "@/methods/pathRedirection";
import { extractCorePermissions } from "@/methods/permissionsHelpers";

// adds the auth and partner routes to the router
const routes: Array<RouteRecordRaw> = [...authRoutes, ...partnerRoutes];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

router.beforeEach((to) => {
    const sessionStore = useSessionStore();

    // user permissions
    const storedUserPermissions = sessionStore.permissions ?? null;
    // meta route properties
    const isAuthenticated = sessionStore.isAuthenticated;

    const requiredPermissions: string[] =
        (to.meta.requiredPermissions as string[]) || [];

    const currentUserPermissions = storedUserPermissions
        ? extractCorePermissions(storedUserPermissions)
        : [];

    const basePermissions = [Permissions.BASE];
    const hasBaseAccess = checkIfArrayElementsMatch(
        basePermissions,
        currentUserPermissions,
    );

    /** Handle authenticated users navigating to root */
    if (isAuthenticated && to.path === "/") {
        if (hasBaseAccess) {
            /** Redirecting to partner transactions order payments */
            return "/partner/transactions/order-payments";
        }

        /** No permissions access - redirecting to unauthorised access **/
        return "/unauthorised-access";
    }

    /** Check if the user permissions list includes the route's required permissions */
    if (requiredPermissions.length > 0) {
        const hasRequiredPermissions = requiredPermissions.every((permission) =>
            currentUserPermissions.includes(permission),
        );

        if (!hasRequiredPermissions) {
            return "/unauthorised-access";
        }
    }
});

export const redirectTo404 = () => {
    router.push({ name: "404" });
};

export const redirectToAccessDenied = () => {
    router.push({ name: "UnauthorisedAccess" });
};

export const changeRoute = (path: string) => {
    router.push(path).catch((err) => {
        console.log("Route error: " + err);
    });
};

export const redirectToRoute = (route: any) => {
    const existingState = { ...history.state };

    router.replace({
        ...route,
        state: existingState,
    });
};

export const navigateBack = (step: number) => {
    /**
     * Navigate back
     * @param {number} step - indicates by how many steps to go backward in the history stack
     */
    router.go(step);
};

export default router;
